<template>
  <div>
    <load-profile />
    <!-- Search Container Card -->
    <n-search-container
      v-model="params"
      :active="true"
      :show-header="false"
      show-search
      :loading="loading"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      >
        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
        <template #state="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-0-5">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>
          <b-col class="text-right">
            <b-row
              align-h="end"
              no-gutters
            >
              <b-col cols="auto">
                <bet-setting-modal :type-id="6" />
              </b-col>
              <b-col
                v-if="$can('create', 'player') && !userData.isVendor"
                class="pl-1"
                cols="auto"
              >
                <b-button
                  variant="primary"
                  :to="{ name: 'create-player' }"
                >
                  {{ $t('button.create') }}
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          @sort-changed="sortChanged"
        >
          <template #cell(username)="{ item }">
            {{ item.username }}
            <small
              v-if="item.secondUsername"
              class="d-block text-wala"
            >
              ({{ item.secondUsername }})
            </small>
          </template>
          <template #cell(phone)="{ item }">
            <template v-if="$can('read','show-phone-number')">
              {{ item.phone }}
            </template>
          </template>
          <template #cell(bank)="{ item }">
            <p
              v-if="item.depositBankName"
              class="mb-0"
            >
              {{ item.depositBankName }}
            </p>
            <p
              v-if="item.depositAccountNumber"
              class="mb-0"
            >
              {{ item.depositAccountNumber }}
            </p>
            <p
              v-if="item.depositAccountHolder"
              class="mb-0"
            >
              {{ item.depositAccountHolder }}
            </p>
          </template>
          <template #cell(ipaddress)="data">
            <b-link
              class="link-underline"
              @click="viewIp(data.item)"
            >
              {{ data.item.ipaddress }}
            </b-link>
          </template>
          <template #cell(givenCredit)="data">
            {{ data.item.givenCredit | currency(data.item.ccy) }}
          </template>
          <template #cell(avalBalance)="data">
            {{ data.item.avalBalance | currency(data.item.ccy) }}
            <div
              v-if="canViewAccrue"
              class="text-draw"
            >
              ({{ data.item.accrueAmount | currency(data.item.ccy) }})
            </div>
            <div
              v-if="canViewPayback"
              class="text-brand"
            >
              ({{ data.item.paybackAmount | currency(data.item.ccy) }})
            </div>
          </template>
          <template #cell(cashBalance)="data">
            {{ data.item.cashBalance | currency(data.item.ccy) }}
          </template>
          <template #cell(isVendor)="data">
            <b-badge
              v-if="data.item.isVendor"
              pill
              variant="info"
              badge-glow
            >
              {{ $t(`general.external`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="primary"
              badge-glow
            >
              {{ $t(`general.internal`) }}
            </b-badge>
            <b-badge
              v-if="data.item.selfRegister"
              pill
              variant="success"
              badge-glow
              class="d-block mt-1"
            >
              {{ $t(`field.selfRegister`) }}
            </b-badge>
          </template>
          <template #cell(status)="data">
            <b-badge
              v-if="data.item.isEnable"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>
          <template #cell(lastActivityAt)="data">
            {{ data.item.lastActivityAt | formatDate }}
          </template>
          <template #cell(createdAt)="data">
            {{ data.item.createdAt | formatDate }}
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                v-if="$can('update', 'player')"
                v-b-tooltip.hover
                :to="{
                  name: 'update-player',
                  params: { id: data.item.id },
                }"
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('create', 'player') &&
                  !data.item.isVendor"
                v-b-tooltip.hover
                :to="{
                  name: 'create-player',
                  query: { id: data.item.id },
                }"
                :title="$t('button.clone')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="CopyIcon" />
              </b-button>
              <b-button
                v-if="
                  ($can('create', 'deposit-withdrawal') || $can('update', 'deposit-withdrawal')) &&
                    !data.item.isVendor
                "
                v-b-tooltip.hover
                :title="$t('button.deposit')"
                :variant="userData.actionBefore ? 'wala' : 'primary'"
                pill
                size="sm"
                @click="
                  showDepositOrWithdrawModel(
                    data.item.id,
                    data.item.username,
                    data.item.avalBalance,
                    data.item.ccy
                  )
                "
              >
                <template v-if="userData.actionBefore">
                  {{ $t(`button.deposit`) }}
                </template>
                <feather-icon
                  v-else
                  icon="CreditCardIcon"
                />
              </b-button>
              <b-button
                v-if="($can('create', 'deposit-withdrawal') || $can('update', 'deposit-withdrawal')) &&
                  !data.item.isVendor"
                v-b-tooltip.hover
                :title="$t('button.withdraw')"
                :variant="userData.actionBefore ? 'meron' : 'primary'"
                pill
                size="sm"
                @click="
                  showDepositOrWithdrawModel(
                    data.item.id,
                    data.item.username,
                    data.item.avalBalance,
                    data.item.ccy,
                    'withdrawal'
                  )
                "
              >
                <template v-if="userData.actionBefore">
                  {{ $t(`button.withdraw`) }}
                </template>
                <feather-icon
                  v-else
                  icon="RotateCcwIcon"
                />
              </b-button>
            </div>
          </template>
        </n-table>
        <n-pagination
          ref="pagination"
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="params.page"
          @change="list"
        />
      </div>
    </b-card>
    <deposit-or-withdraw
      ref="depositWithdrawModel"
      @success="depositOrWithdrawSuccess"
    />
    <ip-modal ref="ipModal" />
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormRadio,
  BFormRadioGroup,
  BLink,
  BRow,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
// eslint-disable-next-line import/no-cycle
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import DepositOrWithdraw from '@/views/account/depositOrWithdraw.vue'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@core/utils/filter'
import IpModal from '@/views/ipModal/Index.vue'
import BetSettingModal from '@/views/betSettingModal/Index.vue'
import { calculateDatesByPeriod } from '@/libs/helper'
import SearchInputs from './searchInput'
import SearchInputAN from './searchInputAN'
import SearchInputCommission from './searchInputCommission'
import TableFields from './tableFields'
import TableFieldsInternal from './tableFieldsInternal'

const PlayerRepository = Repository.get('player')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BFormGroup,
    BFormRadioGroup,
    BFormRadio,
    BBadge,
    BLink,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
    DepositOrWithdraw,
    IpModal,
    BetSettingModal,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        search: this.$route.query.search || '',
        parentId: Number(this.$route.query.parentId) || null,
        startDate: this.$route.query.startDate || null,
        state: this.$route.query.state || null,
        isClaimPayback: this.$route.query.isClaimPayback || false,
      },
      avatarText,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return (
        !!this.$route.query.name
          || !!this.$route.query.username
          || !!this.$route.query.isEnable
          || !!this.$route.query.parentId
      )
    },
    userData() {
      return this.$store.state.profile.name
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem('userData'))
    },
    userId() {
      if (this.$store.state.profile.id) {
        return this.$store.state.profile.id
      }

      return this.userData?.id
    },
    searchFields() {
      const fields = []

      if (this.userData.searchUser) {
        fields.push(...SearchInputs)
      }

      if (this.userData.searchState) {
        fields.push(...SearchInputAN)
      }

      if (this.userData.typeId === 1 || this.userData.isPayback) {
        fields.push(...SearchInputCommission)
      }

      return fields
    },
    fields() {
      if (this.userData.actionBefore) {
        return [...TableFieldsInternal]
      }

      return [...TableFields]
    },
    canViewPayback() {
      return this.$can('update', 'bet-setting') || this.userData.isPayback
    },
    canViewAccrue() {
      return this.$can('update', 'bet-setting') || this.userData.isAccrue
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    'params.period': function (value) {
      const dates = calculateDatesByPeriod(value)
      if (dates.length) {
        // eslint-disable-next-line prefer-destructuring
        this.params.startDate = dates[0]
        // eslint-disable-next-line prefer-destructuring
        this.params.toDate = dates[1]
      }
    },
  },
  mounted() {
    this.getData(this.params.page)
  },
  methods: {
    viewIp(item) {
      this.$refs.ipModal.show(item)
    },
    updateQuerySting() {
      this.$router.replace({ query: this.params })
    },
    list(page = 1) {
      this.params.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy
      this.params.sort = sortDirection
      this.updateQuerySting()
      this.getData(this.params.page)
    },
    search(searchText) {
      this.params.search = searchText
      this.params.page = 1
      this.$refs.pagination.reset()
      this.updateQuerySting()
      this.getData(this.params.page)
    },
    reset() {
      this.$refs.search.reset()
      this.params.search = ''
      this.params.startDate = null
      this.params.toDate = null
      this.params.page = 1
      this.$refs.pagination.reset()
      this.updateQuerySting()
      this.getData(this.page)
    },
    spliceItem(id) {
      const deleteIndex = this.items.findIndex(item => item.id === id)

      this.items.splice(deleteIndex, 1)
      this.total -= 1
    },
    deleteItem(id) {
      PlayerRepository.delete(id)
        .then(() => {
          this.spliceItem(id)
        })
        .catch(() => {
        })
        .then(() => {
          this.loading = false
        })
    },
    getData() {
      this.loading = true
      PlayerRepository.index({
        ...this.params,
        searchFields: this.searchFields,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = [...data]
          this.total = response.data.data.total
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    showDepositOrWithdrawModel(
      id,
      username,
      availableCredit,
      ccy,
      type = 'deposit',
    ) {
      this.$refs.depositWithdrawModel.changeData({
        type,
        username,
        amount: 0,
        promoAmount: 0,
        userId: id,
        remark: null,
        availableCredit,
        ccy,
      })

      this.$refs.depositWithdrawModel.show(
        `${this.$t('field.deposit')} (${username})`,
      )
    },
    depositOrWithdrawSuccess() {
      this.getData()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
